/* eslint-disable react-hooks/exhaustive-deps */
import { InputNumber, Select, TableProps } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { formatCurrency } from 'utils';

import { ReactComponent as IconChevronSort } from 'assets/images/icons/chevron-selector-vertical.svg';
import { ReactComponent as SyncBroken } from 'assets/images/icons/sync-broken.svg';
import { ReactComponent as Sync } from 'assets/images/icons/sync.svg';

import { Button, Modal, Switch, Table } from 'components/common';

import { useOrganization } from 'modules/organization/useOrganization';
import { useXandr } from 'modules/xandr/useXandr';
import { InsertionOrder, LineItem } from 'modules/xandr/xandrApiTypes';

import { useDeviceTypesQuery, useMediaTypesQuery } from '../../modules/apiData/dataApiSlice';
import NotificationService from '../../modules/notifications/notificationService';
import { Option } from '../common/select';
import { XandrConnectionModal } from './XandrConnectionModal';
import { TableData } from './XandrConnectionTypes';
import {
  countRows,
  formatInsertionOrders,
  getYScroll,
  priceTypes,
  renderIfInsertionOrder,
  renderIfLineItem,
  valueOrDashFormatter,
} from './utils';

export const XandrSettings = () => {
  const {
    getInsertionOrders,
    insertionOrders,
    getXandrStatus,
    refreshXandrConnection,
    editInsertionOrdersAndLineItems,
    removeXandrConnection,
    enableInsertionOrder,
    status,
    isLoading,
    syncAllInsertionOrders,
  } = useXandr();

  const { dsp, currentAdvertiserId, currentAdvertiser } = useOrganization();
  const { data: mediaTypes } = useMediaTypesQuery({ dsp });
  const { data: deviceTypes } = useDeviceTypesQuery({ dsp });

  const { height = 0 } = useWindowSize();
  const [isXandrConnectionModalOpen, setIsXandrConnectionModalOpen] = useState(false);
  const [hasDrafts, setHasDrafts] = useState(false);

  const statusId = status?.id;

  const draftIOs = useRef<Record<string, InsertionOrder>>({});
  const draftLIs = useRef<Record<string, LineItem>>({});

  const openXandrConnectionModal = () => setIsXandrConnectionModalOpen(true);
  const closeXandrConnectionModal = () => setIsXandrConnectionModalOpen(false);

  const saveChanges = () => {
    const ios = draftIOs.current;
    const lis = draftLIs.current;

    // Validate budget caps
    for (const io of Object.values(ios)) {
      if (!io.budgetCap) continue;

      if (io.budgetCap < 0) {
        NotificationService.warn('Ensure all budget caps are positive');

        return;
      }
    }

    // Validate CPMs
    for (const li of Object.values(lis)) {
      if (!li.cpm) continue;

      if (li.cpm < 0) {
        NotificationService.warn('Ensure all CPMs are positive');

        return;
      }

      if (li.priceType === 'ADDED' && li.cpm > 0) {
        NotificationService.warn('Non-zero CPM not allowed for line items with \'ADDED\' price type');

        return;
      }
    }

    const ioPayload = Object.entries(ios).map(([id, obj]) => ({
      ...obj,
      id: Number(id),
    }));
    const liPayload = Object.entries(lis).map(([id, obj]) => ({
      ...obj,
      id: Number(id),
    }));

    editInsertionOrdersAndLineItems(ioPayload, liPayload, currentAdvertiserId);
  };

  const handleSync = () => {
    syncAllInsertionOrders();
  };

  useEffect(() => {
    currentAdvertiserId && getXandrStatus();
  }, [currentAdvertiserId]);

  useEffect(() => {
    currentAdvertiserId && getInsertionOrders();
  }, [statusId]);

  useEffect(() => {
    draftIOs.current = {};
    draftLIs.current = {};
  }, [insertionOrders]);

  useEffect(() => {
    setHasDrafts(
      !!insertionOrders?.some((io) => io.draftIO || io.lineItems?.some((li) => li.draftLI)),
    );
  }, [insertionOrders]);

  const rows = useMemo(
    () => (insertionOrders?.length ? formatInsertionOrders(insertionOrders) : []),
    [insertionOrders],
  );

  const rowCount = useMemo(
    () => (insertionOrders?.length ? countRows(insertionOrders) : 0),
    [insertionOrders],
  );

  const ioCell = (v: string, { children, enabled, synced }: TableData) => (
    <div
      className={classNames('pr-3 justify-start items-center gap-2 inline-flex', {
        'pl-6': !children,
      })}
    >
      <div className="grow shrink basis-0 text-base-sm font-normal leading-5">
        {valueOrDashFormatter(v)}
      </div>
      {enabled ? (
        synced ? (
          <Sync className="w-4 h-4 relative" />
        ) : (
          <SyncBroken className="w-4 h-4 relative" />
        )
      ) : null}
    </div>
  );

  const columns: TableProps<TableData>['columns'] = useMemo(
    () => [
      {
        dataIndex: 'enabled',
        title: '',
        fixed: 'left',
        width: 80,
        render: (_, row) =>
          row?.children ? (
            <Switch
              onChange={(checked) => {
                enableInsertionOrder(row.id, checked);
              }}
              checked={row.enabled || false}
            />
          ) : (
            ''
          ),
      },
      {
        dataIndex: 'name',
        fixed: 'left',
        title: 'Xandr IO / Line item',
        key: 'name',
        sorter: (a, b) =>
          a.children && b.children ? ('' + a.name).localeCompare(b.name || '') : 0,
        sortIcon: () => <IconChevronSort width={20} height={20} />,
        showSorterTooltip: false,
        width: 256,
        render: ioCell,
        ellipsis: true,
      },
      {
        dataIndex: 'id',
        key: 'id',
        title: 'IO ID',
        sorter: (a, b) =>
          a.children && b.children ? ('' + a.name).localeCompare(b.name || '') : 0,
        sortIcon: () => <IconChevronSort width={20} height={20} />,
        showSorterTooltip: false,
        width: 128,
        render: renderIfInsertionOrder,
      },
      {
        dataIndex: 'id',
        key: 'lineItemId',
        title: 'Line Item ID',
        width: 128,
        render: renderIfLineItem,
      },
      {
        dataIndex: 'displayName',
        title: 'Campaign name',
        width: 256,
        render: (value, row) => (
          <TextArea
            defaultValue={!row.children ? '-' : value}
            disabled={!row.children}
            onBlur={(e) => {
              draftIOs.current[row.id] = {
                ...draftIOs.current[row.id],
                displayName: e.target.value,
              };
            }}
            autoSize={{ minRows: 1, maxRows: 3 }}
            spellCheck={false}
          />
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
      {
        dataIndex: 'adGroupName',
        title: 'Ad group name',
        width: 256,
        render: (value, row) => (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <TextArea
              defaultValue={row.children ? '-' : value}
              disabled={!!row.children}
              onBlur={(e) => {
                draftLIs.current[row.id] = {
                  ...draftLIs.current[row.id],
                  displayName: e.target.value,
                };
              }}
              autoSize={{ minRows: 1, maxRows: 3 }}
              spellCheck={false}
            />
          </div>
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
      {
        dataIndex: 'audienceName',
        title: 'Audience Name',
        width: 256,
        render: (value, row) => (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <TextArea
              defaultValue={row.children ? '-' : value}
              disabled={!!row.children}
              onBlur={(e) => {
                draftLIs.current[row.id] = {
                  ...draftLIs.current[row.id],
                  audienceName: e.target.value,
                };
              }}
              autoSize={{ minRows: 1, maxRows: 3 }}
              spellCheck={false}
            />
          </div>
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
      {
        dataIndex: 'priceType',
        title: 'Price type',
        width: 172,
        render: (value, row) => (
          <div className="w-full h-full">
            <Select
              className="w-full full-height-select"
              defaultValue={row.children ? '-' : value}
              disabled={!!row.children}
              onChange={(value) => {
                // Add price type for validation later if wasn't modified earlier
                if (!draftLIs.current[row.id]?.cpm) {
                  draftLIs.current[row.id] = {
                    ...draftLIs.current[row.id],
                    cpm: row.cpm,
                  };
                }
                draftLIs.current[row.id] = {
                  ...draftLIs.current[row.id],
                  priceType: value,
                };
              }}
            >
              {Object.values(priceTypes).map(({ value, label }, i) => (
                <Option key={`price-types-${i}`} value={value} className="ctv-option">
                  {label}
                </Option>
              ))}
            </Select>
          </div>
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },

      {
        dataIndex: 'cpm',
        title: 'CPM ',
        width: 128,
        render: (value, row) => (
          <InputNumber
            className="w-full full-height-input"
            defaultValue={row.children ? '-' : value}
            precision={2}
            placeholder={
              row.children ? formatCurrency(0, currentAdvertiser?.currency || 'USD') : '-'
            }
            controls={false}
            disabled={!!row.children}
            onChange={(value) => {
              if (!value) {
                delete draftLIs.current[row.id]?.cpm;
              } else {
                // Add price type for validation later if wasn't modified earlier
                if (!draftLIs.current[row.id]?.priceType) {
                  draftLIs.current[row.id] = {
                    ...draftLIs.current[row.id],
                    priceType: row.priceType,
                  };
                }
                draftLIs.current[row.id] = {
                  ...draftLIs.current[row.id],
                  cpm: Number(value),
                };
              }
            }}
          />
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
      {
        dataIndex: 'budgetCap',
        title: 'Budget cap',
        width: 156,
        render: (value, row) => (
          <InputNumber
            className="w-full full-height-input"
            defaultValue={value ?? '-'}
            controls={false}
            disabled={!row.children}
            precision={2}
            placeholder={
              row.children ? formatCurrency(0, currentAdvertiser?.currency || 'USD') : '-'
            }
            onChange={(value) => {
              if (!value) {
                delete draftIOs.current[row.id]?.budgetCap;
              } else {
                draftIOs.current[row.id] = {
                  ...draftIOs.current[row.id],
                  budgetCap: Number(value),
                };
              }
            }}
          />
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
      {
        dataIndex: 'mediaType',
        title: 'Media type',
        width: 128,
        render: (value, row) => (
          <Select
            className="w-full full-height-select"
            defaultValue={row.children ? '-' : value?.name || '-'}
            disabled={!!row.children}
            onChange={(value) => {
              draftLIs.current[row.id] = {
                ...draftLIs.current[row.id],
                mediaTypeId: value,
              };
            }}
          >
            {mediaTypes?.map(({ id, name }, i) => (
              <Option key={`price-types-${i}`} value={id} className="ctv-option">
                {name}
              </Option>
            ))}
          </Select>
        ),
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
      {
        dataIndex: 'devices',
        title: 'Device type',
        width: 384,
        render: (_, row) => {
          const selectedIds = row.children ? [] : (row.devices?.map((d) => d.id) ?? []);

          return (
            <Select
              mode="multiple"
              maxTagCount={2}
              optionLabelProp="label"
              className="w-full full-height-select"
              defaultValue={selectedIds}
              disabled={!!row.children}
              onChange={(checkedIds: number[]) => {
                draftLIs.current[row.id] = {
                  ...draftLIs.current[row.id],
                  deviceTypeIds: checkedIds,
                };
              }}
            >
              {deviceTypes?.map((d) => (
                <Option key={d.id} value={d.id} label={d.name}>
                  {d.name}
                </Option>
              ))}
            </Select>
          );
        },
        onCell: () => ({
          className: 'editable-cell',
        }),
      },
    ],
    [deviceTypes],
  );

  return (
    <div className="w-full h-full bg-white rounded-xl border border-primary-gray-100 flex-col justify-start items-start flex">
      <div className="p-8 flex-col justify-start items-start inline-flex w-full">
        <div className="flex-col justify-start items-start gap-4 flex w-full">
          <div className="text-secondary-black text-heading-xl font-semibold leading-relaxed">
            Xandr connection
          </div>
          {status ? (
            <div className="w-full justify-between items-start inline-flex">
              <div className="justify-start items-center gap-2 flex flex-wrap xl:flex-nowrap">
                {hasDrafts ? (
                  <Button variant="primary" size="md" onClick={handleSync} isLoading={isLoading}>
                    Sync to dashboard
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  size="md"
                  onClick={refreshXandrConnection}
                  isLoading={isLoading}
                >
                  Refresh Xandr advertiser
                </Button>
                <div className="text-primary-gray-500 text-base-xs font-medium leading-none">
                  Last refreshed on {format(new Date(status?.modificationDate), 'MMM d, yyy')} at{' '}
                  {format(new Date(status?.modificationDate), 'hh:mm aa OOOO')}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="md"
                  className="min-w-auto"
                  isLoading={isLoading}
                  onClick={saveChanges}
                >
                  Save changes
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  className="min-w-auto"
                  isLoading={isLoading}
                  onClick={removeXandrConnection}
                >
                  Remove connection
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={openXandrConnectionModal}
              isLoading={isLoading}
            >
              Connect Xandr advertiser
            </Button>
          )}
          {rows.length ? (
            <div className="grid w-full rounded-xl border border-primary-gray-100 p-4 pb-0">
              <Table
                className="xandr-table"
                loading={isLoading}
                dataSource={rows}
                columns={columns}
                pagination={false}
                scroll={{ x: 2300, ...getYScroll(rowCount, height) }}
                rowKey="id"
                expandable={{
                  defaultExpandAllRows: true,
                  showExpandColumn: false,
                  expandedRowClassName: () => 'ctv-expanded-row',
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <Modal
        open={isXandrConnectionModalOpen}
        footer={null}
        closable={false}
        destroyOnClose
        onCancel={closeXandrConnectionModal}
      >
        <XandrConnectionModal onClose={closeXandrConnectionModal} />
      </Modal>
    </div>
  );
};
